import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Card, 
  Table, 
  Title, 
  Text, 
  Button, 
  Group, 
  Stack, 
  Badge,
  Loader,
  Alert,
  Tabs
} from '@mantine/core';
import { IconArrowLeft, IconHistory, IconInfoCircle, IconTag, IconBinary, IconGauge } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import type { Asset, AssetAllHistory } from '../services/assetsService';
import { getAsset, getAssetHistory, listAssetDatatypes, listAssetValueTypes } from '../services/assetsService';
import { listUserCustomers } from '../services/customersService';

const formatDateTime = (dateString: string) => {
  try {
    const date = new Date(dateString);
    return date.toLocaleString();
  } catch {
    return dateString;
  }
};

const AssetVersionHistoryPage: React.FC = () => {
  const { assetId } = useParams<{ assetId: string }>();
  const navigate = useNavigate();
  const translation = useTranslation();
  const t = translation?.t || ((key: string) => key);
  
  const [asset, setAsset] = useState<Asset | null>(null);
  const [historyData, setHistoryData] = useState<AssetAllHistory | null>(null);
  const [customers, setCustomers] = useState<{ value: string; label: string }[]>([]);
  const [datatypes, setDatatypes] = useState<{ value: string; label: string }[]>([]);
  const [valueTypes, setValueTypes] = useState<{ value: string; label: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = async () => {
    if (!assetId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const [assetData, allHistoryData, customersData, datatypesData, valueTypesData] = await Promise.all([
        getAsset(assetId),
        getAssetHistory(assetId),
        listUserCustomers(),
        listAssetDatatypes(),
        listAssetValueTypes()
      ]);
      
      setAsset(assetData);
      setHistoryData(allHistoryData);
      setCustomers(customersData.map((x: { id: string; company: string }) => ({ value: x.id, label: x.company })));
      setDatatypes(datatypesData.map((dt) => ({ value: dt.id, label: dt.name })));
      setValueTypes(valueTypesData.map((vt) => ({ value: vt.id, label: vt.name })));
    } catch (err) {
      console.error('Error loading asset history:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [assetId]);

  if (loading) {
    return (
      <Stack align="center" justify="center" style={{ minHeight: 400 }}>
        <Loader size="lg" />
        <Text>{t('loading')}</Text>
      </Stack>
    );
  }

  if (error) {
    return (
      <Stack>
        <Group>
          <Button 
            variant="subtle" 
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate('/admin/assets')}
          >
            {t('back_to_assets')}
          </Button>
        </Group>
        <Alert color="red" icon={<IconInfoCircle size={16} />}>
          {error}
        </Alert>
      </Stack>
    );
  }

  if (!asset) {
    return (
      <Stack>
        <Group>
          <Button 
            variant="subtle" 
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate('/admin/assets')}
          >
            {t('back_to_assets')}
          </Button>
        </Group>
        <Alert color="yellow" icon={<IconInfoCircle size={16} />}>
          {t('asset_not_found')}
        </Alert>
      </Stack>
    );
  }

  const versionHistoryRows = historyData?.version_history.map((entry, index) => (
    <Table.Tr key={entry.id}>
      <Table.Td>
        <Group gap="sm">
          <Badge 
            variant="light" 
            color={index === 0 ? 'green' : index < 3 ? 'blue' : 'gray'}
          >
            {entry.version}
          </Badge>
          {index === 0 && (
            <Badge variant="filled" color="green" size="xs">
              {t('current')}
            </Badge>
          )}
        </Group>
      </Table.Td>
      <Table.Td>{formatDateTime(entry.datetime)}</Table.Td>
    </Table.Tr>
  )) || [];

  const nameHistoryRows = historyData?.name_history.map((entry, index) => (
    <Table.Tr key={entry.id}>
      <Table.Td>
        <Group gap="sm">
          <Text fw={500}>{entry.name}</Text>
          {index === 0 && (
            <Badge variant="filled" color="green" size="xs">
              {t('current')}
            </Badge>
          )}
        </Group>
      </Table.Td>
      <Table.Td>{formatDateTime(entry.datetime)}</Table.Td>
    </Table.Tr>
  )) || [];

  const datatypeHistoryRows = historyData?.datatype_history.map((entry, index) => {
    const datatype = datatypes.find(dt => dt.value === entry.datatype.toString());
    const datatypeName = datatype?.label || `ID: ${entry.datatype}`;
    
    return (
      <Table.Tr key={entry.id}>
        <Table.Td>
          <Group gap="sm">
            <Badge variant="light" color="orange">
              {datatypeName}
            </Badge>
            {index === 0 && (
              <Badge variant="filled" color="green" size="xs">
                {t('current')}
              </Badge>
            )}
          </Group>
        </Table.Td>
        <Table.Td>{formatDateTime(entry.datetime)}</Table.Td>
      </Table.Tr>
    );
  }) || [];

  const valueHistoryRows = historyData?.value_history.map((entry, index) => {
    const valueType = valueTypes.find(vt => vt.value === entry.value_type.toString());
    const valueTypeName = valueType?.label || `ID: ${entry.value_type}`;
    
    return (
      <Table.Tr key={entry.id}>
        <Table.Td>
          <Group gap="sm">
            <Badge variant="light" color="violet">
              {valueTypeName}
            </Badge>
            {index === 0 && (
              <Badge variant="filled" color="green" size="xs">
                {t('current')}
              </Badge>
            )}
          </Group>
        </Table.Td>
        <Table.Td>{formatDateTime(entry.datetime)}</Table.Td>
      </Table.Tr>
    );
  }) || [];

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Title order={2}>
          <Group gap="sm">
            <IconHistory size={24} />
            {t('asset_history')}
          </Group>
        </Title>
      </Group>

      <Card withBorder>
        <Stack gap="md">
          <Title order={4}>{t('asset_info')}</Title>
          <Group>
            <div>
              <Text size="sm" c="dimmed">{t('serial_number')}</Text>
              <Text fw={500}>{asset.sn}</Text>
            </div>
            <div>
              <Text size="sm" c="dimmed">{t('type')}</Text>
              <Text fw={500}>{asset.type}</Text>
            </div>
            <div>
              <Text size="sm" c="dimmed">{t('install_name')}</Text>
              <Text fw={500}>{asset.install_name || '-'}</Text>
            </div>
            <div>
              <Text size="sm" c="dimmed">{t('mac_address')}</Text>
              <Text fw={500}>{asset.mac || '-'}</Text>
            </div>
            <div>
              <Text size="sm" c="dimmed">{t('customer')}</Text>
              <Text fw={500}>
                {customers.find(c => c.value === asset.customerId)?.label || asset.customerId || '-'}
              </Text>
            </div>
            <div>
              <Text size="sm" c="dimmed">{t('current_version')}</Text>
              <Text fw={500}>
                {historyData?.version_history[0]?.version || '-'}
              </Text>
            </div>
            <div>
              <Text size="sm" c="dimmed">{t('current_datatype')}</Text>
              <Text fw={500}>
                {(() => {
                  // First try to get from datatype history
                  if (historyData?.datatype_history && historyData.datatype_history.length > 0) {
                    const currentDatatypeId = historyData.datatype_history[0].datatype.toString();
                    const datatype = datatypes.find(dt => dt.value === currentDatatypeId);
                    return datatype?.label || `ID: ${currentDatatypeId}`;
                  }
                  // Fall back to asset's current datatype
                  if (asset.datatype_name) {
                    return asset.datatype_name;
                  }
                  if (asset.datatype_id !== undefined && asset.datatype_id !== null) {
                    const datatypeIdStr = asset.datatype_id.toString();
                    const datatype = datatypes.find(dt => dt.value === datatypeIdStr);
                    return datatype?.label || `ID: ${asset.datatype_id}`;
                  }
                  return '-';
                })()}
              </Text>
            </div>
            <div>
              <Text size="sm" c="dimmed">{t('status')}</Text>
              <Badge 
                variant="light" 
                color={asset.active ? 'green' : 'red'}
              >
                {asset.active ? t('active') : t('inactive')}
              </Badge>
            </div>
          </Group>
        </Stack>
      </Card>

      <Card withBorder>
        <Tabs defaultValue="version">
          <Tabs.List>
            <Tabs.Tab value="version" leftSection={<IconHistory size={16} />}>
              {t('version_history')} ({historyData?.version_history.length || 0})
            </Tabs.Tab>
            <Tabs.Tab value="name" leftSection={<IconTag size={16} />}>
              {t('name_history')} ({historyData?.name_history.length || 0})
            </Tabs.Tab>
            <Tabs.Tab value="datatype" leftSection={<IconBinary size={16} />}>
              {t('datatype_history')} ({historyData?.datatype_history.length || 0})
            </Tabs.Tab>
            <Tabs.Tab value="value" leftSection={<IconGauge size={16} />}>
              {t('value_history')} ({historyData?.value_history.length || 0})
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="version" pt="lg">
            <Stack gap="md">
              <Title order={5}>{t('version_history')}</Title>
              {(!historyData?.version_history || historyData.version_history.length === 0) ? (
                <Alert color="blue" icon={<IconInfoCircle size={16} />}>
                  {t('no_version_history')}
                </Alert>
              ) : (
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>{t('version')}</Table.Th>
                      <Table.Th>{t('date_time')}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {versionHistoryRows}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="name" pt="lg">
            <Stack gap="md">
              <Title order={5}>{t('name_history')}</Title>
              {(!historyData?.name_history || historyData.name_history.length === 0) ? (
                <Alert color="blue" icon={<IconInfoCircle size={16} />}>
                  {t('no_name_history')}
                </Alert>
              ) : (
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>{t('name')}</Table.Th>
                      <Table.Th>{t('date_time')}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {nameHistoryRows}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="datatype" pt="lg">
            <Stack gap="md">
              <Title order={5}>{t('datatype_history')}</Title>
              {(!historyData?.datatype_history || historyData.datatype_history.length === 0) ? (
                <Alert color="blue" icon={<IconInfoCircle size={16} />}>
                  {t('no_datatype_history')}
                </Alert>
              ) : (
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>{t('datatype')}</Table.Th>
                      <Table.Th>{t('date_time')}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {datatypeHistoryRows}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="value" pt="lg">
            <Stack gap="md">
              <Title order={5}>{t('value_history')}</Title>
              {(!historyData?.value_history || historyData.value_history.length === 0) ? (
                <Alert color="blue" icon={<IconInfoCircle size={16} />}>
                  {t('no_value_history')}
                </Alert>
              ) : (
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>{t('value_type')}</Table.Th>
                      <Table.Th>{t('date_time')}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {valueHistoryRows}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Tabs.Panel>
        </Tabs>
      </Card>
    </Stack>
  );
};

export default AssetVersionHistoryPage;
